import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { CURRENT_USER_CLIENTS_LIST_QUERY_KEY } from '@/modules/common/queries/clients.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CUSTOM_CHARTS_DATA_QUERY_KEY,
  CUSTOM_CHARTS_LIST_QUERY_KEY,
} from '@/modules/admin-dashboard/queries/custom-charts.ts';
import { CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY } from '@/modules/admin-dashboard/queries/tasks.ts';
import {
  CreateIncidentResultRequest,
  GetIncidentResultListResponse,
  GetIncidentTypeListResponse,
  GetListV1IncidentResultsGetParams,
  GetListV1IncidentTypesGetParams,
} from 'compliance-api';

export const INCIDENTS_TYPES_LIST_QUERY_KEY = 'compliance-common-incidents-types-list';
export const INCIDENTS_LIST_QUERY_KEY = 'compliance-common-incidents-list';

export const useIncidentsTypesListQuery = (
  payload: GetListV1IncidentTypesGetParams,
  options?: { disabled?: boolean; cacheKey?: string },
) => {
  const { disabled = false, cacheKey } = options || {};
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetIncidentTypeListResponse>({
    queryKey: [INCIDENTS_TYPES_LIST_QUERY_KEY, JSON.stringify(payload), cacheKey, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getListV1IncidentTypesGet(
        {
          ...payload,
          page:
            typeof pageParam === 'number' && pageParam
              ? pageParam
              : pageParam?.page || payload.page,
          filter_by_name: pageParam?.search || payload.filter_by_name,
        },
        [],
      );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useIncidentsListQuery = (
  payload: GetListV1IncidentResultsGetParams,
  disabled = false,
) => {
  const { page, ...data } = payload;
  return useInfiniteQuery<GetIncidentResultListResponse>({
    queryKey: [INCIDENTS_LIST_QUERY_KEY, JSON.stringify(data)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    refetchInterval: 1000 * 60 * 5, //5min
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getListV1IncidentResultsGet({
        ...payload,
        page: typeof pageParam === 'number' && pageParam ? pageParam : pageParam?.page || page,
        filter_by_name: pageParam?.search || payload.filter_by_name,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useCreateIncidentMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateIncidentResultRequest) => {
      const response = await withErrorHandling(api.v1.createV1IncidentResultsPost(payload), {
        loading: 'Reporting event...',
        success: 'Event reported',
      });
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([INCIDENTS_TYPES_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      queryClient.invalidateQueries([CUSTOM_CHARTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CUSTOM_CHARTS_DATA_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_CLIENTS_LIST_QUERY_KEY]);
    },
  });
};
